import React from 'react';
import {
    Paper,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { FilterList as FilterListIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useTypyUdalostiQuery, usePodtypyByTypIdQuery } from '../../api/JsdhNotifikaceService/service/AdminMutations';

export type DatePreset = '30d' | 'letos' | 'vse' | 'lonsky';

export interface FilterValues {
    dateFrom: Dayjs | null;
    dateTo: Dayjs | null;
    typ: string | null;
    podtyp: string | null;
    preset: DatePreset | null;
}

interface StatistikyFilterBarProps {
    values: FilterValues;
    onChange: (values: FilterValues) => void;
}

const StatistikyFilterBar: React.FC<StatistikyFilterBarProps> = ({ values, onChange }) => {
    const { data: typy } = useTypyUdalostiQuery();

    // Resolve typId by matching nazev
    const selectedTypObj = typy?.find((t) => t.nazev === values.typ) ?? null;
    const { data: podtypy } = usePodtypyByTypIdQuery(selectedTypObj?.id ?? undefined);

    const handlePresetChange = (_: React.MouseEvent<HTMLElement>, newPreset: DatePreset | null) => {
        if (!newPreset) return;
        const now = dayjs();
        let dateFrom: Dayjs | null = null;
        let dateTo: Dayjs | null = now;
        switch (newPreset) {
            case '30d':
                dateFrom = now.subtract(30, 'day');
                break;
            case 'letos':
                dateFrom = now.startOf('year');
                break;
            case 'vse':
                dateFrom = null;
                dateTo = null;
                break;
            case 'lonsky':
                dateFrom = now.subtract(1, 'year').startOf('year');
                dateTo = now.subtract(1, 'year').endOf('year');
                break;
        }
        onChange({ ...values, dateFrom, dateTo, preset: newPreset });
    };

    const handleDateFromChange = (v: Dayjs | null) => {
        onChange({ ...values, dateFrom: v, preset: null });
    };

    const handleDateToChange = (v: Dayjs | null) => {
        onChange({ ...values, dateTo: v, preset: null });
    };

    const handleTypChange = (typ: string) => {
        onChange({ ...values, typ: typ || null, podtyp: null });
    };

    const handlePodtypChange = (podtyp: string) => {
        onChange({ ...values, podtyp: podtyp || null });
    };

    return (
        <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12 }}>
                    <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
                    >
                        <FilterListIcon fontSize="small" />
                        Filtr období a typu
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <DatePicker
                        label="Od"
                        value={values.dateFrom}
                        onChange={handleDateFromChange}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <DatePicker
                        label="Do"
                        value={values.dateTo}
                        onChange={handleDateToChange}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <ToggleButtonGroup
                        value={values.preset}
                        exclusive
                        onChange={handlePresetChange}
                        size="small"
                    >
                        <ToggleButton value="30d">30 dní</ToggleButton>
                        <ToggleButton value="letos">Letos</ToggleButton>
                        <ToggleButton value="lonsky">Loňský rok</ToggleButton>
                        <ToggleButton value="vse">Vše</ToggleButton>
                    </ToggleButtonGroup>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Typ události</InputLabel>
                        <Select
                            value={values.typ ?? ''}
                            onChange={(e) => handleTypChange(e.target.value as string)}
                            label="Typ události"
                        >
                            <MenuItem value="">Všechny typy</MenuItem>
                            {typy?.map((t) => (
                                <MenuItem key={t.id} value={t.nazev}>
                                    {t.nazev}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControl fullWidth size="small" disabled={!values.typ}>
                        <InputLabel>Podtyp události</InputLabel>
                        <Select
                            value={values.podtyp ?? ''}
                            onChange={(e) => handlePodtypChange(e.target.value as string)}
                            label="Podtyp události"
                        >
                            <MenuItem value="">Všechny podtypy</MenuItem>
                            {podtypy?.map((p) => (
                                <MenuItem key={p.id} value={p.nazev}>
                                    {p.nazev}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default StatistikyFilterBar;
